import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../hooks/useTheme';
import { BookingCalendar } from './BookingCalendar';
import type { CalendarTexts } from './BookingCalendar';

export default function BookDemo() {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isLight = theme === 'light';

  const bg = isLight ? '#f0f6ff' : '#020817';
  const cardBg = isLight ? '#ffffff' : 'rgba(12,22,48,0.70)';
  const border = isLight ? '#c5d4e8' : 'rgba(255,255,255,0.08)';
  const textMain = isLight ? '#0a1628' : '#ffffff';
  const textSub = isLight ? 'rgba(10,22,40,0.55)' : 'rgba(255,255,255,0.50)';

  const calTexts: CalendarTexts = {
    stepDate: t('cal_step_date'),
    stepTime: t('cal_step_time'),
    stepForm: t('cal_step_form'),
    prevMonth: t('cal_prev'),
    nextMonth: t('cal_next'),
    noSlots: t('cal_no_slots'),
    backToDate: t('cal_back_date'),
    modifyTime: t('cal_modify_time'),
    yourBooking: t('cal_your_booking'),
    dateLabel: t('cal_date_label'),
    timeLabel: t('cal_time_label'),
    labelName: t('cal_name_label'),
    labelPhone: t('cal_phone_label'),
    labelEmail: t('cal_email_label'),
    labelBizType: t('cal_biz_label'),
    labelDemoMode: t('cal_demo_mode_label'),
    demoModeRemote: t('cal_demo_mode_remote'),
    demoModeInPerson: t('cal_demo_mode_in_person'),
    placeholderName: t('cal_name_placeholder'),
    placeholderPhone: t('cal_phone_placeholder'),
    placeholderEmail: t('cal_email_placeholder'),
    placeholderBizType: t('cal_biz_placeholder'),
    bizTypeOptions: [
      t('cal_biz_restaurant'),
      t('cal_biz_supermarket'),
      t('cal_biz_hardware'),
      t('cal_biz_minimarket'),
      t('cal_biz_distributor'),
      t('cal_biz_loan'),
      t('cal_biz_events'),
      t('cal_biz_tech'),
      t('cal_biz_other'),
    ],
    submit: t('cal_submit'),
    submitting: t('cal_submitting'),
    successTitle: t('cal_success_title'),
    successMsg: t('cal_success_msg'),
    successEmailMsg: t('cal_success_email_msg'),
    errorMsg: t('cal_error'),
    loading: t('cal_loading'),
    legendAvailable: t('cal_legend_available'),
    legendSelected: t('cal_legend_selected'),
    slotDuration: t('cal_slot_duration'),
    sessionLimitMsg: t('cal_session_limit'),
    confirmationTitle: t('cal_confirm_title'),
    confirmationDate: t('cal_confirm_date'),
    confirmationTime: t('cal_confirm_time'),
    confirmationName: t('cal_confirm_name'),
    confirmationPhone: t('cal_confirm_phone'),
    confirmationEmail: t('cal_confirm_email'),
    confirmationMode: t('cal_confirm_mode'),
    confirmationBizType: t('cal_confirm_biz'),
    confirmationNote: t('cal_confirm_note'),
    confirmationNewBooking: t('cal_confirm_new'),
    weekDays: [t('cal_wd_sun'), t('cal_wd_mon'), t('cal_wd_tue'), t('cal_wd_wed'), t('cal_wd_thu'), t('cal_wd_fri'), t('cal_wd_sat')],
    months: [t('cal_m1'), t('cal_m2'), t('cal_m3'), t('cal_m4'), t('cal_m5'), t('cal_m6'), t('cal_m7'), t('cal_m8'), t('cal_m9'), t('cal_m10'), t('cal_m11'), t('cal_m12')],
    slotsWeekFew: t('cal_slots_week_few'),
    slotsWeekSome: t('cal_slots_week_some'),
    slotsWeekMany: t('cal_slots_week_many'),
    urgencyLast: t('cal_urgency_last'),
    urgencyFew: t('cal_urgency_few'),
    waNotifyBtn: t('cal_wa_notify_btn'),
    labelPhoneIsWhatsapp: t('cal_phone_is_whatsapp'),
    appointmentTypeLabel: t('cal_appt_type_label'),
    appointmentTypeOptions: [
      t('cal_appt_demo'),
      t('cal_appt_consultoria'),
      t('cal_appt_proyecto'),
      t('cal_appt_web'),
      t('cal_appt_otro'),
    ],
    confirmApptType: t('cal_confirm_appt_type'),
    appointmentTypeOtherPlaceholder: t('cal_appt_otro_placeholder'),
  };

  return (
    <section id="book-demo" className="relative py-28 px-6 overflow-x-hidden" style={{ background: bg }}>
      {/* glow */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at center, rgba(0,207,255,0.07) 0%, transparent 70%)' }}
      />

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-electric text-sm font-semibold tracking-widest uppercase font-body mb-3">
            {t('book_label')}
          </span>
          <h2 className="font-heading text-4xl md:text-5xl font-bold mb-4" style={{ color: textMain }}>
            {t('book_title_1')} <span className="text-electric">{t('book_title_2')}</span>
          </h2>
          <p className="font-body text-base max-w-xl mx-auto leading-relaxed" style={{ color: textSub }}>
            {t('book_sub')}
          </p>
        </div>

        {/* Main card */}
        <div
          className="rounded-3xl border overflow-hidden"
          style={{ backgroundColor: cardBg, borderColor: border }}
        >
          <div className="grid md:grid-cols-2 gap-0">

            {/* LEFT — CTA */}
            <div className="p-10 lg:p-14 flex flex-col justify-center gap-8">
              <div className="flex flex-col gap-6">
                <div
                  className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full font-body w-fit"
                  style={{
                    backgroundColor: isLight ? 'rgba(0,207,255,0.12)' : 'rgba(0,207,255,0.15)',
                    color: '#00cfff',
                  }}
                >
                  <i className="ri-calendar-schedule-line text-sm" />
                  {t('book_badge')}
                </div>

                <div>
                  <h3
                    className="font-heading text-3xl font-bold leading-tight mb-4"
                    style={{ color: textMain }}
                  >
                    {t('book_cta_heading')}
                  </h3>
                  <p className="font-body text-sm leading-relaxed" style={{ color: textSub }}>
                    {t('book_cta_desc')}
                  </p>
                </div>

                {/* CTA Button */}
                <a
                  href="#book-demo"
                  className="inline-flex items-center gap-2.5 bg-electric hover:bg-electric/85 text-navy-950 font-heading font-bold text-sm px-6 py-3.5 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap hover:scale-105 active:scale-95 w-fit"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-calendar-check-line text-base" />
                  </div>
                  {t('book_cta_btn')}
                </a>
              </div>
            </div>

            {/* RIGHT — Booking Calendar */}
            <div
              className="p-8 lg:p-12 flex flex-col border-t md:border-t-0 md:border-l"
              style={{ borderColor: border, backgroundColor: isLight ? '#f8fbff' : 'rgba(255,255,255,0.02)' }}
            >
              <BookingCalendar texts={calTexts} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
